import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ChevronLeft, ChevronRight, Volume2, VolumeX, Pause, Play } from 'lucide-react';
import './VideoPortal.css';

const VideoPortal = ({
                         video,
                         onClose,
                         onPrevious,
                         onNext,
                         totalItems,
                         currentIndex
                     }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const videoRef = useRef(null);

    const handleKeyDown = (e) => {
        e.stopPropagation();
        switch (e.key) {
            case 'ArrowLeft':
                if (onPrevious) onPrevious();
                break;
            case 'ArrowRight':
                if (onNext) onNext();
                break;
            case 'Escape':
                onClose();
                break;
            case ' ':
                togglePlay(e);
                break;
            case 'm':
                toggleMute(e);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onPrevious, onNext]);

    const handleDownload = (e) => {
        e.stopPropagation();
        const filename = video.split('/').pop();
        const link = document.createElement('a');
        link.href = video;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const togglePlay = (e) => {
        if (e) e.stopPropagation();
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const toggleMute = (e) => {
        if (e) e.stopPropagation();
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(!isMuted);
        }
    };

    // Handler for video end
    const handleVideoEnded = () => {
        setIsPlaying(false);
    };

    // Handler for video play/pause events
    const handleVideoStateChange = () => {
        if (videoRef.current) {
            setIsPlaying(!videoRef.current.paused);
        }
    };

    return createPortal(
        <div className="portal-overlay" onClick={onClose}>
            <div className="portal-content" onClick={e => e.stopPropagation()}>
                <div className="portal-buttons">
                    <button
                        onClick={togglePlay}
                        className="portal-button"
                        aria-label={isPlaying ? "Pause" : "Play"}
                    >
                        {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                    </button>
                    <button
                        onClick={toggleMute}
                        className="portal-button"
                        aria-label={isMuted ? "Unmute" : "Mute"}
                    >
                        {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                    </button>
                    <button
                        onClick={handleDownload}
                        className="portal-button"
                        aria-label="Download video"
                    >
                        <Download size={24} />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        className="portal-button"
                        aria-label="Close"
                    >
                        <X size={24} />
                    </button>
                    <div className="image-counter">
                        {currentIndex + 1} / {totalItems}
                    </div>
                </div>
                <div className="portal-image-container">
                    <button
                        className="portal-nav-button portal-nav-button-left"
                        onClick={(e) => {
                            e.stopPropagation();
                            onPrevious();
                        }}
                        aria-label="Previous item"
                    >
                        <ChevronLeft size={48}/>
                    </button>

                    <div className="video-wrapper">
                        <video
                            ref={videoRef}
                            src={video}
                            className="portal-video"
                            controls
                            playsInline
                            onClick={togglePlay}
                            onPlay={handleVideoStateChange}
                            onPause={handleVideoStateChange}
                            onEnded={handleVideoEnded}
                        />
                    </div>

                    <button
                        className="portal-nav-button portal-nav-button-right"
                        onClick={(e) => {
                            e.stopPropagation();
                            onNext();
                        }}
                        aria-label="Next item"
                    >
                        <ChevronRight size={48}/>
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default VideoPortal;